/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

import React, { ReactElement } from "react"
import { observer } from "mobx-react-lite"
import { Button } from "@mui/material"
import Loading from "@medaica/common/components/loading"
import { BookingAppointment } from "@medaica/common/types"
import { useBookingStores } from "../booking-context"
import BookingLayout from "../components/booking-layout"
import bookingCopy from "../bookingCopy"
import { bookingColors } from "../booking-theme"

const CHIP_BASE = "inline-block rounded-md px-2 py-0.5 text-xs font-medium"
const STATUS_STYLES: Record<string, string> = {
  scheduled: "bg-blue-100 text-blue-700",
  completed: "bg-green-100 text-green-700",
  no_show: "bg-gray-100 text-gray-600",
  cancelled: "bg-red-100 text-red-700",
}

const formatWhen = (slotStart: Date, timeZone: string): string => {
  try {
    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      timeZone,
      timeZoneName: "short",
    }).format(slotStart)
  } catch {
    return slotStart.toLocaleString()
  }
}

const formatExpiry = (expiry: Date | null): string | null => {
  if (!expiry) {
    return null
  }
  try {
    return new Intl.DateTimeFormat("en-US", { month: "long", year: "numeric" }).format(expiry)
  } catch {
    return null
  }
}

const StatusChip = ({ status }: { status: BookingAppointment["status"] }): ReactElement => (
  <span className={`${CHIP_BASE} ${STATUS_STYLES[status] ?? "bg-gray-100 text-gray-600"}`}>
    {bookingCopy.account.statusLabels[status] ?? status}
  </span>
)

const Account = observer((): ReactElement => {
  const { sessionStore, uiStore, wizardStore } = useBookingStores()
  const account = sessionStore.account
  const copy = bookingCopy.account

  if (!account) {
    return (
      <BookingLayout>
        <div className="py-24 text-center">
          {sessionStore.accountLoadError ? (
            <>
              <p className="text-sm text-gray-600">{copy.loadError}</p>
              <Button variant="outlined" sx={{ mt: 2 }} onClick={() => void sessionStore.loadAccount()}>
                {copy.retry}
              </Button>
            </>
          ) : (
            <Loading />
          )}
        </div>
      </BookingLayout>
    )
  }
  // Name is nullable (a buyer row can have no name) — drop empty parts instead of
  // interpolating "null" into the header.
  const headerRight = [account.buyerFirstName, sessionStore.maskedEmail].filter(Boolean).join(" · ")
  const poolLine = copy.examsAvailable(account.examsAvailable, formatExpiry(account.earliestExpiry))
  const hasCredits = account.examsAvailable > 0

  const startWizard = (): void => {
    wizardStore.reset()
    uiStore.goTo("patient")
  }

  return (
    <BookingLayout headerRight={headerRight}>
      <div className="max-w-3xl mx-auto px-6 py-10">
        <h1 className="text-2xl font-bold" style={{ color: bookingColors.primary }}>
          {copy.greeting(account.buyerFirstName || "there")}
        </h1>
        {hasCredits && <p className="mt-1 text-gray-600">{poolLine}</p>}

        {hasCredits ? (
          <Button variant="contained" size="large" fullWidth sx={{ mt: 3 }} onClick={startWizard}>
            {copy.schedule}
          </Button>
        ) : (
          <p className="mt-3 text-gray-600">{copy.noCredits}</p>
        )}

        <div className="mt-8 bg-white rounded-lg shadow-sm p-5">
          <h2 className="text-xs font-semibold tracking-wide text-gray-500 uppercase">{copy.bookedHeading}</h2>
          {account.appointments.length === 0 ? (
            <p className="mt-3 text-sm text-gray-500">{copy.noBookings}</p>
          ) : (
            <>
              <div className="mt-3 sm:hidden">
                {/* Below `sm` the 5-column table hid 60% of itself (incl. the Join link — the row's
                  only action) behind a sideways scroll; phones get stacked cards instead. */}
                <ul className="divide-y divide-gray-100">
                  {account.appointments.map((appointment) => (
                    <li key={appointment.examRef} className="py-3 text-sm">
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-medium text-gray-800">{appointment.patientName}</span>
                        <StatusChip status={appointment.status} />
                      </div>
                      <div className="mt-1 text-gray-600">
                        {/* The table's column headers provide this context on desktop — keep it
                            for screen readers here even though the visual design omits it. */}
                        <span className="sr-only">{copy.columns.when}: </span>
                        {formatWhen(appointment.slotStart, appointment.timeZone)}
                      </div>
                      <div className="mt-1 flex items-center justify-between gap-2">
                        <span className="text-gray-500">
                          <span className="sr-only">{copy.columns.confirmationId}: </span>
                          {appointment.examRef}
                        </span>
                        {appointment.videoRoomUrl ? (
                          <a
                            className="py-1 font-medium text-primary"
                            href={appointment.videoRoomUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {copy.joinLink}
                          </a>
                        ) : null}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="mt-3 hidden overflow-x-auto sm:block">
                {/* On desktop the original 5-column table renders unchanged (wider than minWidth). */}
                <table className="w-full text-sm" style={{ minWidth: 480 }}>
                  <thead>
                    <tr className="text-left text-gray-500">
                      <th className="py-2 font-medium">{copy.columns.who}</th>
                      <th className="py-2 font-medium">{copy.columns.when}</th>
                      <th className="py-2 font-medium">{copy.columns.confirmationId}</th>
                      <th className="py-2 font-medium">{copy.columns.status}</th>
                      <th aria-label={copy.joinLink} />
                    </tr>
                  </thead>
                  <tbody>
                    {account.appointments.map((appointment) => (
                      <tr key={appointment.examRef} className="border-t border-gray-100">
                        <td className="py-3 text-gray-800">{appointment.patientName}</td>
                        <td className="py-3 text-gray-800">
                          {formatWhen(appointment.slotStart, appointment.timeZone)}
                        </td>
                        <td className="py-3 text-gray-800">{appointment.examRef}</td>
                        <td className="py-3">
                          <StatusChip status={appointment.status} />
                        </td>
                        <td className="py-3 text-right">
                          {appointment.videoRoomUrl ? (
                            <a
                              className="font-medium text-primary"
                              href={appointment.videoRoomUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {copy.joinLink}
                            </a>
                          ) : null}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>

        <div className="mt-5 flex items-center justify-between bg-white rounded-lg shadow-sm px-5 py-4">
          <span className="text-sm text-gray-600">{copy.supportPrompt}</span>
          <a className="text-sm font-medium text-primary" href={`mailto:${bookingCopy.supportEmail}`}>
            {copy.supportCta}
          </a>
        </div>
      </div>
    </BookingLayout>
  )
})

export default Account
